import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../../api';
import { useAuth } from '../../context/AuthProvider';
import './CommandPalette.scss';

const normalize = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Ctrl/Cmd+K palette opened from the sidebar: jump to a page, or search products and patients
// (same /products and /getPatients endpoints as their listings), all from the keyboard.
// pages: [{ label, icon (tabler class), to }] — already filtered by the user's rights.
const CommandPalette = ({ open, onClose, pages }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRight } = useAuth();
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState({ products: [], patients: [] });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setRemote({ products: [], patients: [] });
    setActive(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // Debounced product / patient search; stale responses are dropped via the abort signal.
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRemote({ products: [], patients: [] });
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      const get = (url, right) => (hasRight(right)
        ? axios.get(url, { withCredentials: true, signal: controller.signal, params: { page: 0, size: 5, keyword: q } })
          .then(({ data }) => data?.content ?? [])
          .catch(() => [])
        : Promise.resolve([]));
      Promise.all([
        get(`${API_BASE_URL}/api/v1/products`, 'PRODUCTS_VIEW'),
        get(`${API_BASE_URL}/api/v1/getPatients`, 'PATIENTS_VIEW'),
      ]).then(([products, patients]) => {
        if (controller.signal.aborted) return;
        setRemote({ products, patients });
        setLoading(false);
      });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, open, hasRight]);

  const sections = useMemo(() => {
    const q = normalize(query.trim());
    const pageEntries = pages
      .filter((p) => !q || normalize(p.label).includes(q))
      .slice(0, q ? 6 : 8)
      .map((p) => ({ key: `page-${p.to}`, icon: p.icon, label: p.label, to: p.to }));
    const productEntries = remote.products.map((p) => ({
      key: `product-${p.id}`,
      icon: 'ti-vaccine-bottle',
      label: p.name,
      sub: [p.presentation, p.dosage].filter(Boolean).join(' · '),
      meta: p.publicPrice != null ? `${Number(p.publicPrice).toFixed(2)} dhs` : null,
      to: `/products/${p.id}`,
    }));
    const patientEntries = remote.patients.map((p) => ({
      key: `patient-${p.id}`,
      icon: 'ti-user',
      label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
      sub: p.telephone || p.email || '',
      to: `/patient/${p.id}`,
    }));
    return [
      { title: t('command.pages'), entries: pageEntries },
      { title: t('command.products'), entries: productEntries },
      { title: t('command.patients'), entries: patientEntries },
    ].filter((s) => s.entries.length > 0);
  }, [query, pages, remote, t]);

  const flat = useMemo(() => sections.flatMap((s) => s.entries), [sections]);

  useEffect(() => { setActive(0); }, [query]);

  // Keep the highlighted entry scrolled into view while moving with the arrows.
  useEffect(() => {
    listRef.current?.querySelector('.cmdk__item.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const go = (entry) => {
    if (!entry) return;
    onClose();
    navigate(entry.to);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let index = -1;
  return createPortal(
    <div className="cmdk-overlay" onMouseDown={onClose}>
      <div className="cmdk" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cmdk__search">
          <i className="ti ti-search" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('command.placeholder')}
            spellCheck={false}
          />
          {loading && <span className="spinner-border spinner-border-sm text-secondary" />}
          <kbd>Esc</kbd>
        </div>

        <div className="cmdk__list" ref={listRef}>
          {sections.map((section) => (
            <div key={section.title} className="cmdk__section">
              <div className="cmdk__section-title">{section.title}</div>
              {section.entries.map((entry) => {
                index += 1;
                const i = index;
                return (
                  <button
                    key={entry.key}
                    type="button"
                    className={`cmdk__item${i === active ? ' is-active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(entry)}
                  >
                    <span className="cmdk__item-icon"><i className={`ti ${entry.icon}`} /></span>
                    <span className="cmdk__item-text">
                      <span className="cmdk__item-label">{entry.label}</span>
                      {entry.sub && <span className="cmdk__item-sub">{entry.sub}</span>}
                    </span>
                    {entry.meta && <span className="cmdk__item-meta">{entry.meta}</span>}
                    <i className="ti ti-corner-down-left cmdk__item-enter" />
                  </button>
                );
              })}
            </div>
          ))}
          {flat.length === 0 && !loading && (
            <div className="cmdk__empty">{t('command.empty')}</div>
          )}
        </div>

        <div className="cmdk__footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> {t('command.navigate')}</span>
          <span><kbd>↵</kbd> {t('command.open')}</span>
          <span><kbd>Esc</kbd> {t('command.close')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;
